// src/pages/TrebleTownMap.jsx
// ✅ Treble Town Road Map — standalone page
// Reads from unlockRules + TrophyStorage + StarQuestBar localStorage key
// CSS/SVG winding path — no image required
// Route: /treble-town-map

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getMaxUnlockedLevel } from "../services/unlockRules";
import { TrophyStorage } from "../services/trophyStorage";

/* ══════════════════════════════════════════════════════
   LEVEL METADATA
   ══════════════════════════════════════════════════════ */
const LEVELS = [
  { id: 1,  name: "Doron Attacks",      song: "Twinkle Twinkle",       emoji: "🐉", ringColor: "#60C8E8", glowColor: "#b3e8f8" },
  { id: 2,  name: "Zeb's Birthday",     song: "Happy Birthday",         emoji: "🦓", ringColor: "#FFB347", glowColor: "#ffe4b3" },
  { id: 3,  name: "Penguin's Melt",     song: "Ode to Joy",             emoji: "🐧", ringColor: "#70C7A0", glowColor: "#c5f0e0" },
  { id: 4,  name: "Treb Lake",          song: "Machli Jal Ki Rani",     emoji: "🐟", ringColor: "#C17EE0", glowColor: "#ead6f8" },
  { id: 5,  name: "Ren's Connect",      song: "Jingle Bells",           emoji: "🦌", ringColor: "#FF7070", glowColor: "#ffd0d0" },
  { id: 6,  name: "Spider Dance",       song: "Incy Wincy Spider",      emoji: "🕷️", ringColor: "#5DC88A", glowColor: "#c5f0d6" },
  { id: 7,  name: "Bamboo Forest",      song: "London Bridge",          emoji: "🎋", ringColor: "#38B2AC", glowColor: "#c0ece8" },
  { id: 8,  name: "Peacock Parade",     song: "National Anthem",        emoji: "🦚", ringColor: "#F6C143", glowColor: "#fef0c0" },
  { id: 9,  name: "Crocodile Test",     song: "Row Row Row Your Boat",  emoji: "🐊", ringColor: "#4CAF50", glowColor: "#c8eec8" },
  { id: 10, name: "Final Doron Battle", song: "??? — Find the ancient melody", emoji: "👹", ringColor: "#E53935", glowColor: "#ffc0c0", isBoss: true },
];

/* ══════════════════════════════════════════════════════
   WINDING PATH — 360 × 940 viewBox
   Zigzag: Lv1 (bottom-center) → Lv10 (top-center / boss)
   ══════════════════════════════════════════════════════ */
const NODES = [
  { id: 1,  cx: 180, cy: 876 },
  { id: 2,  cx: 72,  cy: 766 },
  { id: 3,  cx: 288, cy: 660 },
  { id: 4,  cx: 72,  cy: 560 },
  { id: 5,  cx: 288, cy: 466 },
  { id: 6,  cx: 72,  cy: 378 },
  { id: 7,  cx: 288, cy: 296 },
  { id: 8,  cx: 72,  cy: 214 },
  { id: 9,  cx: 288, cy: 132 },
  { id: 10, cx: 180, cy: 52  },
];

// Smooth cubic bezier path through every node
const PATH_D = [
  "M 180 876",
  "C 180 836 72 806 72 766",
  "C 72 726 288 700 288 660",
  "C 288 618 72 598 72 560",
  "C 72 520 288 500 288 466",
  "C 288 424 72 408 72 378",
  "C 72 338 288 326 288 296",
  "C 288 254 72 244 72 214",
  "C 72 172 288 162 288 132",
  "C 288 90 180 78 180 52",
].join(" ");

/* ══════════════════════════════════════════════════════
   STAR QUEST KEY (mirrors StarQuestBar.jsx)
   ══════════════════════════════════════════════════════ */
const STARQUEST_KEY = "tt_starquest_completed_levels_v1";

function getStarLevels() {
  try {
    const raw = localStorage.getItem(STARQUEST_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.map(Number));
  } catch {
    return new Set();
  }
}

function getLevelPath(id) {
  return `/level-${id}`;
}

/* ══════════════════════════════════════════════════════
   MAIN PAGE
   ══════════════════════════════════════════════════════ */
export default function TrebleTownMap() {
  const navigate = useNavigate();
  const [lockedTip, setLockedTip] = useState(null); // id of tapped locked node

  const { maxUnlocked, levelsCompleted, starLevels } = useMemo(() => {
    const maxUnlocked = Math.max(1, Number(getMaxUnlockedLevel?.() || 1));
    const s = TrophyStorage.getState?.() || {};
    const rip = s.roundInProgress || {};
    const doneMap = rip.levelsCompleted || {};
    const done = new Set(
      Object.keys(doneMap).filter(k => doneMap[k]).map(k => Number(k))
    );
    return { maxUnlocked, levelsCompleted: done, starLevels: getStarLevels() };
  }, []);

  function getStatus(id) {
    if (levelsCompleted.has(id)) return "complete";
    if (id === maxUnlocked) return "current";
    if (id < maxUnlocked) return "unlocked";
    return "locked";
  }

  function handleNodeClick(e, level) {
    e.stopPropagation();
    const status = getStatus(level.id);
    if (status === "locked") {
      setLockedTip(lockedTip === level.id ? null : level.id);
      return;
    }
    navigate(getLevelPath(level.id));
  }

  // Count completed + stars for the header summary
  const doneCount = levelsCompleted.size;
  const starCount = starLevels.size;

  return (
    <div className="ttm-root" onClick={() => setLockedTip(null)}>
      {/* ── Fixed sky layer ── */}
      <div className="ttm-sky-fixed" />
      <Clouds />
      <FloatingNotes />

      {/* ── Sticky header ── */}
      <header className="ttm-header">
        <button className="ttm-back" onClick={() => navigate("/game-menu")}>←</button>
        <div className="ttm-header-center">
          <span className="ttm-header-title">🗺️ Treble Town</span>
          <span className="ttm-header-sub">
            {doneCount}/10 complete · ⭐ {starCount}/8 stars
          </span>
        </div>
        <div style={{ width: 40 }} />
      </header>

      {/* ── Scrollable map area ── */}
      <div className="ttm-scroll-area">
        <div className="ttm-map-wrap">
          <svg
            viewBox="0 0 360 940"
            xmlns="http://www.w3.org/2000/svg"
            className="ttm-svg"
            style={{ overflow: "visible" }}
          >
            <defs>
              <filter id="ttm-shadow" x="-30%" y="-30%" width="160%" height="160%">
                <feDropShadow dx="0" dy="3" stdDeviation="5" floodOpacity="0.18"/>
              </filter>
              <filter id="ttm-boss-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="8" result="blur"/>
                <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              <radialGradient id="ttm-boss-halo" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#ff5252" stopOpacity="0.55"/>
                <stop offset="100%" stopColor="#ff5252" stopOpacity="0"/>
              </radialGradient>
              <linearGradient id="ttm-sky" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#aee8f8"/>
                <stop offset="60%"  stopColor="#cef5d8"/>
                <stop offset="100%" stopColor="#9dd86a"/>
              </linearGradient>
              <linearGradient id="ttm-hill-dark" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#7ec850"/>
                <stop offset="100%" stopColor="#5aad30"/>
              </linearGradient>
            </defs>

            {/* ── Background ── */}
            <rect width={360} height={940} fill="url(#ttm-sky)" />
            <BackgroundDecos />

            {/* ── Path — 3 layers: shadow, cream track, dash overlay ── */}
            <path d={PATH_D} fill="none" stroke="rgba(0,0,0,0.13)" strokeWidth={18}
              strokeLinecap="round" transform="translate(1,5)" />
            <path d={PATH_D} fill="none" stroke="#d8c49a" strokeWidth={18} strokeLinecap="round" />
            <path d={PATH_D} fill="none" stroke="#ede3c8" strokeWidth={11} strokeLinecap="round" />
            <path d={PATH_D} fill="none" stroke="rgba(255,255,255,0.6)" strokeWidth={5}
              strokeLinecap="round" strokeDasharray="10 14" />

            {/* ── Level nodes ── */}
            {LEVELS.map((level, i) => {
              const node   = NODES[i];
              const status = getStatus(level.id);
              const hasStar = starLevels.has(level.id) ||
                (status === "complete" && level.id >= 2 && level.id <= 9);
              return (
                <MapNode
                  key={level.id}
                  level={level}
                  node={node}
                  status={status}
                  hasStar={hasStar}
                  showTip={lockedTip === level.id}
                  maxUnlocked={maxUnlocked}
                  onClick={(e) => handleNodeClick(e, level)}
                />
              );
            })}
          </svg>
        </div>
      </div>

      {/* ── Legend ── */}
      <footer className="ttm-legend">
        <LegendPip color="#6BCB77" label="Done" />
        <LegendPip color="#FFD93D" label="Current" />
        <LegendPip color="#60a5fa" label="Unlocked" />
        <LegendPip color="#ccc"    label="Locked" />
        <LegendPip color="#E53935" label="Boss" />
      </footer>

      <style>{TTM_STYLES}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MAP NODE
   ══════════════════════════════════════════════════════ */
function MapNode({ level, node, status, hasStar, showTip, maxUnlocked, onClick }) {
  const { cx, cy } = node;
  const isBoss   = !!level.isBoss;
  const isLocked = status === "locked";
  const r        = isBoss ? 42 : 32;

  // Colour scheme per status
  const C = {
    complete: { ring: "#52b788", bg: "#f0faf4", label: "#1b5e38" },
    current:  { ring: "#f4a629", bg: "#fffbec", label: "#7c5600" },
    unlocked: { ring: "#3b82f6", bg: "#eff6ff", label: "#1e3a8a" },
    locked:   { ring: "#b0b0b0", bg: "#f5f5f5", label: "#888"    },
  };
  // Boss overrides
  if (isBoss && status !== "locked") {
    C.complete = C.current = C.unlocked = { ring: "#c62828", bg: "#fff0f0", label: "#7f0000" };
  }
  const c = C[status] || C.locked;

  return (
    <g
      transform={`translate(${cx},${cy})`}
      style={{ cursor: isLocked ? "default" : "pointer" }}
      onClick={onClick}
    >
      {/* Boss halo pulse */}
      {isBoss && !isLocked && (
        <circle r={r + 22} fill="url(#ttm-boss-halo)" className="ttm-boss-pulse" />
      )}

      {/* Current level outer ring pulse */}
      {status === "current" && (
        <circle r={r + 10} fill="none" stroke={c.ring} strokeWidth={3}
          opacity={0.65} className="ttm-cur-pulse" />
      )}

      {/* Drop shadow */}
      <circle r={r + 3} fill="rgba(0,0,0,0.14)" transform="translate(0,5)" />

      {/* Coloured ring */}
      <circle r={r + 3} fill={c.ring} />

      {/* White/cream inner */}
      <circle r={r} fill={c.bg} />

      {/* Tint fill when complete */}
      {status === "complete" && (
        <circle r={r} fill={c.ring} opacity={0.18} />
      )}

      {/* Emoji or lock */}
      <text
        textAnchor="middle"
        dominantBaseline="middle"
        y={isLocked ? 2 : (isBoss ? -6 : -5)}
        fontSize={isLocked ? 17 : isBoss ? 26 : 20}
        style={{ userSelect: "none" }}
      >
        {isLocked ? "🔒" : level.emoji}
      </text>

      {/* Level label inside node */}
      {!isLocked && (
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          y={isBoss ? 16 : 13}
          fontSize={isBoss ? 12 : 11}
          fontWeight="900"
          fontFamily="'Fredoka One', 'Nunito', sans-serif"
          fill={c.label}
          style={{ userSelect: "none" }}
        >
          {isBoss ? "BOSS" : `Lv ${level.id}`}
        </text>
      )}

      {/* Star badge */}
      {hasStar && (
        <text
          textAnchor="middle"
          y={r + 18}
          fontSize={15}
          style={{ userSelect: "none" }}
        >
          ⭐
        </text>
      )}

      {/* Name label */}
      {!isLocked && (
        <text
          textAnchor="middle"
          y={r + (hasStar ? 34 : 18)}
          fontSize={isBoss ? 12 : 10}
          fontWeight="800"
          fontFamily="'Fredoka One', 'Nunito', sans-serif"
          fill={isBoss ? "#7f0000" : "#374151"}
          style={{ userSelect: "none" }}
        >
          {level.name}
        </text>
      )}

      {/* Song sub-label (complete/current only) */}
      {(status === "complete" || status === "current") && !isBoss && (
        <text
          textAnchor="middle"
          y={r + (hasStar ? 46 : 30)}
          fontSize={9}
          fontFamily="'Nunito', sans-serif"
          fill="#6b7280"
          style={{ userSelect: "none" }}
        >
          {level.song}
        </text>
      )}

      {/* Locked tooltip bubble */}
      {showTip && (
        <g>
          <rect x={-78} y={-r - 60} width={156} height={48} rx={10}
            fill="#1f2937" opacity={0.93} />
          {/* Arrow */}
          <polygon points="0,0 -8,-12 8,-12"
            fill="#1f2937" opacity={0.93}
            transform={`translate(0,${-r - 12})`} />
          <text textAnchor="middle" y={-r - 42}
            fontSize={11} fontWeight="800"
            fontFamily="'Nunito', sans-serif" fill="#fff"
            style={{ userSelect: "none" }}>
            {level.name}
          </text>
          <text textAnchor="middle" y={-r - 26}
            fontSize={10} fontFamily="'Nunito', sans-serif" fill="#fca5a5"
            style={{ userSelect: "none" }}>
            Beat Level {level.id - 1} to unlock
          </text>
        </g>
      )}
    </g>
  );
}

/* ══════════════════════════════════════════════════════
   SVG BACKGROUND DECORATIONS
   ══════════════════════════════════════════════════════ */
function BackgroundDecos() {
  // Hills at the bottom
  // Small tree SVG helper
  const Tree = ({ x, y, s = 1, shade = "#52aa40" }) => (
    <g transform={`translate(${x},${y}) scale(${s})`}>
      <rect x={-4} y={8} width={8} height={20} fill="#8B5E3C" rx={3} />
      <ellipse cx={0} cy={-2} rx={18} ry={22} fill={shade === "#52aa40" ? "#3d8c2f" : shade} />
      <ellipse cx={0} cy={-5} rx={14} ry={17} fill={shade} />
      <ellipse cx={-4} cy={-10} rx={7} ry={9} fill={shade === "#52aa40" ? "#72cc58" : "#8de07a"} opacity={0.7}/>
    </g>
  );

  // Small rock
  const Rock = ({ x, y }) => (
    <ellipse cx={x} cy={y} rx={9} ry={6} fill="#bbb" opacity={0.5} />
  );

  return (
    <g>
      {/* Rolling hills (layered) */}
      <path d="M0 840 Q60 800 120 820 Q180 840 240 810 Q300 780 360 810 L360 940 L0 940 Z"
        fill="#a8df7a" />
      <path d="M0 880 Q90 855 180 870 Q270 855 360 875 L360 940 L0 940 Z"
        fill="#8ecf5a" />
      <path d="M0 910 Q90 898 180 906 Q270 898 360 908 L360 940 L0 940 Z"
        fill="#7ec850" />

      {/* Mid-ground hill lumps */}
      <ellipse cx={60}  cy={940} rx={120} ry={60} fill="#9fda72" />
      <ellipse cx={300} cy={940} rx={130} ry={55} fill="#92d468" />

      {/* Trees left side */}
      <Tree x={18}  y={720} s={0.72} shade="#52aa40" />
      <Tree x={30}  y={500} s={0.68} shade="#4a9e3c" />
      <Tree x={18}  y={312} s={0.74} shade="#52aa40" />
      <Tree x={30}  y={140} s={0.66} shade="#3e9238" />

      {/* Trees right side */}
      <Tree x={338} y={630} s={0.70} shade="#4ea83a" />
      <Tree x={328} y={418} s={0.76} shade="#52aa40" />
      <Tree x={338} y={238} s={0.68} shade="#46a036" />

      {/* Rocks */}
      <Rock x={155} y={905} />
      <Rock x={220} y={898} />
      <Rock x={100} y={918} />
      <Rock x={270} y={912} />

      {/* Flowers scattered */}
      {[
        [148, 900, "#ff9eb5"], [200, 895, "#ffe066"],
        [240, 905, "#a8e6cf"], [120, 912, "#ffb3ba"],
        [280, 895, "#ff9eb5"],
      ].map(([x, y, color], i) => (
        <g key={i} transform={`translate(${x},${y})`}>
          <circle r={4} fill={color} />
          <circle r={2.5} fill="#ffe066" />
        </g>
      ))}

      {/* Distant mountain silhouette at top */}
      <path d="M0 200 Q60 140 120 180 Q170 120 220 170 Q270 100 320 160 Q350 140 360 170 L360 220 L0 220 Z"
        fill="#c5e8f0" opacity={0.5} />
      <path d="M0 220 Q60 160 120 200 Q170 140 220 190 Q270 120 320 180 Q350 160 360 185 L360 240 L0 240 Z"
        fill="#d8f0f8" opacity={0.35} />

      {/* Doron's Castle scaffold (top area) */}
      <g transform="translate(180, 52)" opacity={0.12}>
        <rect x={-30} y={-70} width={60} height={80} rx={4} fill="#555" />
        <rect x={-34} y={-80} width={16} height={20} rx={2} fill="#555" />
        <rect x={-8}  y={-82} width={16} height={22} rx={2} fill="#555" />
        <rect x={18}  y={-80} width={16} height={20} rx={2} fill="#555" />
      </g>
    </g>
  );
}

/* ══════════════════════════════════════════════════════
   FLOATING CLOUDS (CSS, fixed)
   ══════════════════════════════════════════════════════ */
function Clouds() {
  const clouds = [
    { top: "5%",  left: "4%",  w: 100, delay: 0,  dur: 32 },
    { top: "10%", left: "60%", w: 130, delay: 8,  dur: 28 },
    { top: "18%", left: "22%", w: 85,  delay: 4,  dur: 35 },
    { top: "28%", left: "70%", w: 110, delay: 15, dur: 30 },
  ];
  return (
    <div className="ttm-clouds" aria-hidden>
      {clouds.map((c, i) => (
        <div key={i} className="ttm-cloud"
          style={{ top: c.top, left: c.left, width: c.w,
            animationDelay: `${c.delay}s`, animationDuration: `${c.dur}s` }}
        />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   FLOATING MUSIC NOTES (CSS, fixed)
   ══════════════════════════════════════════════════════ */
function FloatingNotes() {
  const notes = ["♪", "♫", "♩", "♬", "♪", "♫", "♩"];
  return (
    <div className="ttm-notes" aria-hidden>
      {notes.map((n, i) => (
        <span key={i} className="ttm-note"
          style={{
            left: `${6 + i * 13}%`,
            fontSize: 13 + (i % 3) * 5,
            animationDelay: `${i * 1.1}s`,
            animationDuration: `${7 + i * 0.9}s`,
          }}>
          {n}
        </span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   LEGEND PIP
   ══════════════════════════════════════════════════════ */
function LegendPip({ color, label }) {
  return (
    <span className="ttm-legend-item">
      <span className="ttm-legend-dot" style={{ background: color }} />
      {label}
    </span>
  );
}

/* ══════════════════════════════════════════════════════
   ALL CSS
   ══════════════════════════════════════════════════════ */
const TTM_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@700;800;900&display=swap');

  .ttm-root {
    min-height: 100vh;
    font-family: 'Nunito', sans-serif;
    overflow-x: hidden;
    position: relative;
    background: linear-gradient(180deg, #aee8f8 0%, #cdf5d2 55%, #8ecf5a 100%);
  }

  /* ── Fixed sky layer ── */
  .ttm-sky-fixed {
    position: fixed;
    inset: 0;
    background: linear-gradient(180deg, #b3ecfd 0%, #d6f8e2 50%, #9edd72 100%);
    z-index: 0;
    pointer-events: none;
  }

  /* ── Sticky header ── */
  .ttm-header {
    position: sticky;
    top: 0;
    z-index: 200;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    background: rgba(255,255,255,0.88);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border-bottom: 2px solid rgba(255,255,255,0.7);
    box-shadow: 0 2px 14px rgba(0,0,0,0.07);
  }
  .ttm-back {
    width: 40px;
    height: 36px;
    background: #fff;
    border: 2px solid #e5e7eb;
    border-radius: 10px;
    font-size: 17px;
    font-weight: 900;
    color: #4b5563;
    cursor: pointer;
    transition: background 0.15s, transform 0.1s;
    line-height: 1;
  }
  .ttm-back:hover  { background: #f3f4f6; }
  .ttm-back:active { transform: scale(0.94); }

  .ttm-header-center {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
  }
  .ttm-header-title {
    font-family: 'Fredoka One', sans-serif;
    font-size: 20px;
    color: #166534;
    letter-spacing: 0.4px;
    text-shadow: 0 1px 3px rgba(255,255,255,0.8);
  }
  .ttm-header-sub {
    font-size: 11px;
    font-weight: 800;
    color: #6b7280;
    letter-spacing: 0.3px;
  }

  /* ── Map scroll area ── */
  .ttm-scroll-area {
    position: relative;
    z-index: 1;
    display: flex;
    justify-content: center;
    padding: 20px 10px 48px;
  }
  .ttm-map-wrap {
    width: 100%;
    max-width: 440px;
  }
  .ttm-svg {
    width: 100%;
    height: auto;
    display: block;
  }

  /* ── Clouds ── */
  .ttm-clouds {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .ttm-cloud {
    position: absolute;
    height: 38px;
    border-radius: 50px;
    background: rgba(255,255,255,0.88);
    box-shadow: 0 4px 20px rgba(255,255,255,0.4);
    animation: ttm-cloud-drift linear infinite;
  }
  .ttm-cloud::before {
    content: '';
    position: absolute;
    top: -15px; left: 18%;
    width: 52%; height: 30px;
    border-radius: 50%;
    background: inherit;
  }
  .ttm-cloud::after {
    content: '';
    position: absolute;
    top: -9px; right: 18%;
    width: 36%; height: 20px;
    border-radius: 50%;
    background: inherit;
  }
  @keyframes ttm-cloud-drift {
    0%   { transform: translateX(-180px); opacity: 0; }
    6%   { opacity: 1; }
    90%  { opacity: 1; }
    100% { transform: translateX(110vw);  opacity: 0; }
  }

  /* ── Floating notes ── */
  .ttm-notes {
    position: fixed;
    inset: 0;
    z-index: 0;
    pointer-events: none;
    overflow: hidden;
  }
  .ttm-note {
    position: absolute;
    bottom: -8%;
    color: rgba(22,101,52,0.42);
    animation: ttm-note-float linear infinite;
  }
  @keyframes ttm-note-float {
    0%   { transform: translateY(0) rotate(-8deg);    opacity: 0; }
    8%   { opacity: 0.75; }
    80%  { opacity: 0.5; }
    100% { transform: translateY(-108vh) rotate(8deg); opacity: 0; }
  }

  /* ── Node animations ── */
  @keyframes ttm-cur-pulse {
    0%, 100% { transform: scale(1);   opacity: 0.8; }
    50%       { transform: scale(1.35); opacity: 0.2; }
  }
  .ttm-cur-pulse {
    animation: ttm-cur-pulse 1.7s ease-in-out infinite;
    transform-origin: center;
    transform-box: fill-box;
  }

  @keyframes ttm-boss-pulse {
    0%, 100% { transform: scale(1);   opacity: 0.55; }
    50%       { transform: scale(1.25); opacity: 0.15; }
  }
  .ttm-boss-pulse {
    animation: ttm-boss-pulse 2.2s ease-in-out infinite;
    transform-origin: center;
    transform-box: fill-box;
  }

  /* ── Legend ── */
  .ttm-legend {
    position: relative;
    z-index: 10;
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px 14px;
    padding: 10px 16px 28px;
  }
  .ttm-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 11px;
    font-weight: 800;
    color: #374151;
  }
  .ttm-legend-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    display: inline-block;
    flex-shrink: 0;
  }
`;